import React from 'react';
import { DollarSign, Clock, Wallet } from 'lucide-react';
import { formatCurrency, formatDate, getRegistrationFeeLabel } from '../../../utils/helpers';

const TransactionAuditReport = ({
  expenses,
  filteredWallet,
  players,
  games,
  walletTransactions,
  formatCurrency: formatCurrencyProp,
  clubName
}) => {
  const format = formatCurrencyProp || formatCurrency;

  // Separate Initial Donations (receivables) from actual expenses
  const registrationFees = expenses.filter(e => e.isRegistrationFee || e.isInitialDonation);
  const actualExpenses = expenses.filter(e => !e.isRegistrationFee && !e.isInitialDonation);

  const approvedRegFees = registrationFees.filter(e => e.approvalStatus === 'approved');
  const approvedExpenses = actualExpenses.filter(e => e.approvalStatus === 'approved');
  const pendingExpenses = actualExpenses.filter(e => e.approvalStatus === 'pending');
  const deniedExpenses = actualExpenses.filter(e => e.approvalStatus === 'denied');

  return (
    <div className="space-y-6">
      {/* Summary Stats - Now properly categorized */}
      <div className="grid grid-cols-5 gap-4">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-700 mb-1">{getRegistrationFeeLabel({ name: clubName })}s</p>
          <p className="text-2xl font-bold text-blue-900">
            {approvedRegFees.length}
          </p>
          <p className="text-xs text-blue-600 mt-1">
            {format(approvedRegFees.reduce((sum, e) => sum + e.amount, 0))}
          </p>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm text-green-700 mb-1">Approved Expenses</p>
          <p className="text-2xl font-bold text-green-900">
            {approvedExpenses.length}
          </p>
          <p className="text-xs text-green-600 mt-1">
            {format(approvedExpenses.reduce((sum, e) => sum + e.amount, 0))}
          </p>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-sm text-yellow-700 mb-1">Pending</p>
          <p className="text-2xl font-bold text-yellow-900">
            {pendingExpenses.length}
          </p>
          <p className="text-xs text-yellow-600 mt-1">
            {format(pendingExpenses.reduce((sum, e) => sum + e.amount, 0))}
          </p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700 mb-1">Denied</p>
          <p className="text-2xl font-bold text-red-900">
            {deniedExpenses.length}
          </p>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <p className="text-sm text-purple-700 mb-1">Total Transactions</p>
          <p className="text-2xl font-bold text-purple-900">
            {expenses.length}
          </p>
        </div>
      </div>

      {/* Initial Donations Section (Receivables) */}
      {approvedRegFees.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-blue-50 border-b border-blue-200 p-4">
            <h3 className="text-lg font-bold text-blue-900 flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              {getRegistrationFeeLabel({ name: clubName })}s - Receivables ({approvedRegFees.length})
            </h3>
            <p className="text-sm text-blue-700 mt-1">
              💡 These are dues owed TO the club by players - not expenses
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date Assigned</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Player</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount Due</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned By</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {approvedRegFees
                  .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                  .map(fee => {
                    const playerId = fee.splitAmong?.[0];
                    const player = players.find(p => p.id === playerId || p.userId === playerId);
                    const playerName = player?.name || 'Unknown Player';

                    const payment = walletTransactions.find(txn =>
                      txn.userId === playerId &&
                      txn.type === 'payment' &&
                      Math.abs(txn.amount) >= fee.amount
                    );
                    const isPaid = !!payment;

                    return (
                      <tr key={fee.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {formatDate(fee.createdAt)}
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {playerName}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-blue-700 text-right">
                          {format(fee.amount)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {fee.createdByName || fee.createdBy || 'Admin'}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {isPaid ? (
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-semibold">
                              ✓ Paid
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold">
                              Unpaid
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          <div className="bg-blue-50 border-t border-blue-200 p-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-blue-900">Total Receivables:</span>
              <span className="text-lg font-bold text-blue-900">
                {format(approvedRegFees.reduce((sum, e) => sum + e.amount, 0))}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Approved Expenses (Actual costs) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-green-50 border-b border-green-200 p-4">
          <h3 className="text-lg font-bold text-green-900 flex items-center gap-2">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            Approved Expenses ({approvedExpenses.length})
          </h3>
          <p className="text-sm text-green-700 mt-1">
            💡 These are actual costs spent by the club (match fees, meals, equipment, etc.)
          </p>
        </div>
        {approvedExpenses.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p>No approved expenses yet</p>
            <p className="text-sm mt-1">Expenses like match fees, meals, and equipment will appear here</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date Submitted</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Game</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted By</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Split Among</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Approved Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {approvedExpenses
                  .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                  .map(expense => {
                    const game = games.find(g => g.id === expense.gameId);
                    const submitterName = expense.createdByName || expense.addedByPlayerName ||
                      players.find(p => p.id === expense.addedByPlayerId)?.name ||
                      expense.createdBy || 'Unknown';

                    return (
                      <tr key={expense.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {formatDate(expense.createdAt)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{expense.type || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate">
                          {expense.description || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {game?.name || 'No game'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {submitterName}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">
                          {format(expense.amount)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 text-right">
                          {expense.splitAmong?.length || 0} players
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {expense.approvedAt ? formatDate(expense.approvedAt) : '-'}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        )}
        {approvedExpenses.length > 0 && (
          <div className="bg-green-50 border-t border-green-200 p-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-green-900">Total Expenses:</span>
              <span className="text-lg font-bold text-green-900">
                {format(approvedExpenses.reduce((sum, e) => sum + e.amount, 0))}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Pending Expenses */}
      {pendingExpenses.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-yellow-50 border-b border-yellow-200 p-4">
            <h3 className="text-lg font-bold text-yellow-900 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Pending Approval ({pendingExpenses.length})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date Submitted</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Game</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted By</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Days Waiting</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {pendingExpenses
                  .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
                  .map(expense => {
                    const game = games.find(g => g.id === expense.gameId);

                    let submitterDisplay = expense.createdByName || expense.addedByPlayerName || expense.submittedByName;

                    if (!submitterDisplay) {
                      let submitter = players.find(p => p.id === expense.addedByPlayerId) ||
                        players.find(p => p.id === expense.submittedBy) ||
                        players.find(p => p.id === expense.userId) ||
                        players.find(p => p.email === expense.createdBy);

                      if (submitter) {
                        submitterDisplay = submitter.name;
                      } else if (expense.createdBy) {
                        submitterDisplay = expense.createdBy;
                      } else {
                        submitterDisplay = 'System/Admin';
                      }
                    }

                    const daysWaiting = Math.floor((Date.now() - new Date(expense.createdAt).getTime()) / (1000 * 60 * 60 * 24));

                    return (
                      <tr key={expense.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {formatDate(expense.createdAt)}
                          <div className="text-xs text-gray-500">
                            {new Date(expense.createdAt).toLocaleTimeString()}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{expense.type || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate">
                          {expense.description || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {game?.name || 'No game'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {submitterDisplay}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">
                          {format(expense.amount)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${daysWaiting > 7 ? 'bg-red-100 text-red-800' :
                            daysWaiting > 3 ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                            {daysWaiting} {daysWaiting === 1 ? 'day' : 'days'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Denied Expenses */}
      {deniedExpenses.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="bg-red-50 border-b border-red-200 p-4">
            <h3 className="text-lg font-bold text-red-900 flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              Denied Expenses ({deniedExpenses.length})
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date Submitted</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Game</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Submitted By</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Denial Reason</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Denied Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {deniedExpenses
                  .sort((a, b) => new Date(b.deniedAt || b.createdAt) - new Date(a.deniedAt || a.createdAt))
                  .map(expense => {
                    const game = games.find(g => g.id === expense.gameId);
                    const submitterName = expense.createdByName || expense.addedByPlayerName ||
                      players.find(p => p.id === expense.addedByPlayerId)?.name ||
                      expense.createdBy || 'Unknown';

                    return (
                      <tr key={expense.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {formatDate(expense.createdAt)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{expense.type || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate">
                          {expense.description || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {game?.name || 'No game'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {submitterName}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">
                          {format(expense.amount)}
                        </td>
                        <td className="px-4 py-3 text-sm text-red-700 max-w-xs">
                          {expense.denialReason || 'No reason provided'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {expense.deniedAt ? formatDate(expense.deniedAt) : '-'}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Wallet Transactions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="bg-purple-50 border-b border-purple-200 p-4">
          <h3 className="text-lg font-bold text-purple-900 flex items-center gap-2">
            <Wallet className="w-5 h-5" />
            Reimbursements ({filteredWallet.length})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Player</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredWallet
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .map(txn => {
                  const player = players.find(p => p.id === txn.userId);
                  return (
                    <tr key={txn.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {formatDate(txn.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {player?.name || 'Unknown'}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-green-600 text-right">
                        {format(txn.amount)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {txn.notes || '-'}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default TransactionAuditReport;